import React, { FC, useEffect, useState } from 'react';
import { CustomSwitch } from 'components';
import { EditIconBlue, OutlinedInfoIcon } from 'assets/icons';
import { ReminderConfig } from 'components/reminder-modal/types';
import { ReminderDateTypeLabel } from 'containers/settings/constants';
import { calculateScheduledDate, validateReminderPrerequisites } from 'containers/estimates/utils';
import { formatReminderText } from 'containers/settings/utils';
import DateUtils from 'utils/dateUtils';
import { useAppSelector } from 'hooks';
import { selectEstimateCustomer, selectExpiryDate } from 'containers/estimates/estimatesSlice';
import { InfoType } from 'containers/invoices/create-invoice/constants';

interface EstimateFormRemindersSectionProps {
  reminders: ReminderConfig[];
  isEnabled: boolean;
  onToggle: () => void;
  onOpenModal: () => void;
}

const EstimateFormRemindersSection: FC<EstimateFormRemindersSectionProps> = ({
  reminders,
  isEnabled,
  onToggle,
  onOpenModal
}) => {
  const [attemptedToggle, setAttemptedToggle] = useState(false);

  const estimateCustomer = useAppSelector(selectEstimateCustomer);
  const expiryDate = useAppSelector(selectExpiryDate);
  const customerEmail = estimateCustomer?.email || '';
  const hasCustomer = !!estimateCustomer;
  const expiryDateString = expiryDate?.toISOString() || '';

  const { canEnable, errorMessage, msgType } = validateReminderPrerequisites(customerEmail, expiryDate);

  // Reset attemptedToggle when email becomes available or customer is removed
  useEffect(() => {
    if (canEnable || !hasCustomer) {
      setAttemptedToggle(false);
    }
  }, [canEnable, hasCustomer]);

  const handleToggle = () => {
    if (!canEnable && !isEnabled) {
      // User tried to enable without email - show error
      setAttemptedToggle(true);
      return;
    }
    onToggle();
  };

  // Show error when: (1) customer exists and email missing OR (2) user attempted to toggle
  const shouldShowError = (hasCustomer && !canEnable) || (attemptedToggle && !canEnable);

  return (
    <div className="w-full">
      <div className="text-xl font-bold text-primaryText">Estimate reminders</div>
      <div className="mt-2 rounded-md border border-borderGray py-6 px-8">
        <div className="mt-4 flex items-start gap-3">
          <CustomSwitch
            enabled={isEnabled}
            toggleSwitch={handleToggle}
            id="estimate-form-reminders-switch"
            className="mt-1"
          />
          <div className="flex-1">
            <div className={`text-sbase font-semibold text-primaryText ${isEnabled ? '' : 'opacity-70'}`}>
              Send reminders for this estimate
            </div>
            {/* Show error when customer exists with missing email OR user tried to toggle */}
            {shouldShowError && (
              <div
                className={`mt-2 flex w-fit items-center gap-[5px]
               ${
                 msgType === InfoType.ERROR ? '' : 'rounded-md border border-secondary bg-[#A4D7FA4D]'
               } py-1 px-[5.5px]`}>
                <OutlinedInfoIcon
                  className={`mt-0.5 h-[15px] w-[15px] shrink-0 ${
                    msgType === InfoType.ERROR ? 'path path-fill-current text-error' : ''
                  }`}
                />
                <span className={`text-sbase ${msgType === InfoType.ERROR ? 'text-error' : 'text-primaryText'}`}>
                  {errorMessage}
                </span>
              </div>
            )}
          </div>
        </div>

        {isEnabled && (
          <div className="mt-4 rounded-lg border border-borderGray bg-white px-4 pb-5 pt-2">
            <>
              {reminders.length > 0 ? (
                reminders.map((reminder, index) => {
                  // Use reminderDate from response if available (edit flow), otherwise calculate it
                  const scheduledDate =
                    reminder.reminderDate ||
                    calculateScheduledDate(expiryDateString, reminder.scheduleRelation, reminder.days);
                  const isNotLast = index < reminders.length - 1;
                  return (
                    <div
                      key={reminder.id}
                      className={`grid grid-cols-[auto_1fr_auto] gap-4 py-3 ${
                        isNotLast ? 'border-b border-borderGray' : ''
                      }`}>
                      <span className="text-sbase font-medium text-primaryText">
                        Reminder {reminder.reminderNumber}
                      </span>
                      <span className="text-center text-sbase text-accentText">
                        {formatReminderText(reminder, ReminderDateTypeLabel.EXPIRY_DATE)}
                      </span>
                      <span className="text-sbase text-primaryText">
                        {scheduledDate
                          ? DateUtils.getDateInFormat({
                              date: new Date(scheduledDate),
                              dateFormat: 'MMMM dd, yyyy'
                            })
                          : '-'}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="py-4 text-center">
                  <span className="text-sbase text-accentText">No reminders configured</span>
                </div>
              )}
              <div className="flex justify-end">
                <button
                  onClick={onOpenModal}
                  className="mt-2 flex items-center gap-1 text-sbase font-semibold
                   text-secondary hover:text-secondary/80">
                  <EditIconBlue className="h-4 w-4" />
                  {reminders?.length > 0 ? 'Edit reminders' : 'Add reminders'}
                </button>
              </div>
            </>
          </div>
        )}
      </div>
    </div>
  );
};

export default EstimateFormRemindersSection;
